import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  Column,
  GenericEntityType,
  GenericProviderType,
  KindEnum,
  NotFoundException,
  Pagination,
  Provider,
  TemplateEntity,
  WbSafeNunjucksApi,
  catalogEntityAdapter,
  getLabelAndValue,
  parseDisplayField,
  selectTemplateEntity,
} from '@agilelab/plugin-wb-platform';
import { contextProviderFactory } from '../../utils';
import { useColumns } from '../../hooks';
import { useApi } from '@mainstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@mainstage/plugin-catalog-react';
import { UsersFilters, UsersFiltersSection } from './UserFilters';
import { parseEntityRef } from '@mainstage/catalog-model';
import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import { calculatedRef } from '../../../WbGeneralPurposeEntityPicker/utils';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';

export type UserTypeEntity = GenericEntityType & { ref: string };

interface UserContextType
  extends GenericProviderType<UserTypeEntity[], UsersFilters> {}

const [useUserContext, UserContextProvider] =
  contextProviderFactory<UserContextType>();

export default useUserContext;

export interface UserProviderProps {
  templateKind: TemplateEntity | undefined;
  children: React.ReactNode;
  parentTemplateRef?: string;
  fetchUsers?: CatalogApi['queryEntities'];
}

const defaultColumns = new Map<string, string>([
  ['name', '{{spec.profile.displayName}}'],
  [
    'description',
    '{% if spec.profile.email %}{{spec.profile.email}}{% else %}-{% endif %}',
  ],
  ['ref', '{{ref}}'],
]);

const toUserTypeEntity = (user: any): UserTypeEntity => {
  return {
    ...user,
    ref: expandEntityRef(calculatedRef(user)),
    __metadata: {
      kind: KindEnum.user,
      name: user.metadata.name,
    },
  };
};

export const UserProvider: React.FC<UserProviderProps> = props => {
  const { templateKind, children, fetchUsers } = props;
  const catalogApi = useApi(catalogApiRef);
  const queryEntities = fetchUsers ?? catalogApi.queryEntities.bind(catalogApi);
  const [filters, setFilters] = useState<UsersFilters>({});
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);
  const [count, setCount] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 10,
    currentPage: 0,
  });

  const columns: Column[] = useColumns(defaultColumns, templateKind);

  const usersState = useAsync(async () => {
    const result = await queryEntities({
      limit: pagination.limit,
      cursor: pagination.cursor,
      fullTextFilter: {
        term: filters.search!,
        fields: ['spec.profile.displayName', 'metadata.name'],
      },
      filter: {
        kind: ['user'],
      },
    });
    setNextCursor(result.pageInfo.nextCursor);
    setPrevCursor(result.pageInfo.prevCursor);
    setCount(result.totalItems);
    return result.items.map(i => toUserTypeEntity(i as any));
  }, [templateKind, filters, pagination.limit, pagination.cursor]);

  return (
    <UserContextProvider
      value={{
        entitiesState: usersState,
        columns,
        filters,
        changeFilters: <K extends keyof UsersFilters>(
          key: K,
          filterValue: UsersFilters[K],
        ) => {
          setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
          setFilters(f => ({ ...f, [key]: filterValue }));
        },
        resetFilters: () => {
          setFilters({});
        },
        templateKind,
        pagination,
        setPagination,
        count,
        nextCursor,
        prevCursor,
      }}
    >
      {children}
    </UserContextProvider>
  );
};

export class UserHandlerProvider
  implements Provider<UserTypeEntity[], UsersFilters>
{
  private readonly templateEntity: TemplateEntity | undefined;
  private readonly nunjucksApi: WbSafeNunjucksApi;
  private readonly catalogApi: CatalogApi;
  private readonly fetchUsers?: CatalogApi['queryEntities'];

  constructor(
    nunjucksApi: WbSafeNunjucksApi,
    catalogApi: CatalogApi,
    availableKinds: TemplateEntity[],
    fetchUsers?: CatalogApi['queryEntities'],
  ) {
    this.nunjucksApi = nunjucksApi;
    this.catalogApi = catalogApi;
    this.fetchUsers = fetchUsers;

    this.templateEntity = selectTemplateEntity(availableKinds, KindEnum.user);
  }

  useContext = useUserContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: React.ReactNode,
    parentTemplateRef?: string,
  ) {
    return (
      <UserProvider
        templateKind={templateKind}
        parentTemplateRef={parentTemplateRef}
        fetchUsers={this.fetchUsers}
      >
        {children}
      </UserProvider>
    );
  }

  filtersSection = UsersFiltersSection;

  adapt(entity: GenericEntityType) {
    const parsedDisplayField = parseDisplayField(
      this.nunjucksApi,
      entity,
      this.templateEntity?.displayField,
    );
    const catalogEntity = catalogEntityAdapter(
      entity,
      parsedDisplayField,
      true,
    );

    return catalogEntity;
  }

  async resolve(ref: string) {
    const resolvedRef = transformUrnToWitboostId(ref) || ref;
    const parsedEntityRef = parseEntityRef(resolvedRef);
    try {
      if (parsedEntityRef.kind === 'user') {
        const entity = await this.catalogApi.getEntityByRef(resolvedRef);
        if (entity)
          return getLabelAndValue(
            toUserTypeEntity(entity),
            this.adapt(toUserTypeEntity(entity)),
            this.templateEntity,
          );

        throw new NotFoundException(
          `Cannot find a User entity with ref: ${ref}`,
        );
      }

      return undefined;
    } catch (error) {
      throw new Error(`Error while resolving entity with ref: ${ref}`);
    }
  }
}
